import type { Metric, MetricEntry, Scorecard } from './types';

/**
 * Metric rows always arrive with their values embedded, so a metric and the
 * numbers on it are one round trip rather than two.
 */
export const METRIC_SELECT = '*, metric_entries(period, value, entered_by, entered_at)';

/**
 * Values come from `metric_entries` rows, embedded by the select rather than
 * fetched separately. A payload that carries no rows — a realtime `metrics`
 * UPDATE, for instance — means "not loaded", and the caller keeps what it has.
 */
const entriesFromRows = (rows: any): MetricEntry[] =>
  (Array.isArray(rows) ? rows : [])
    .map((r: any) => ({
      period: r.period,
      value: r.value === null || r.value === undefined ? null : Number(r.value),
      enteredBy: r.entered_by ?? null,
      enteredAt: r.entered_at ?? null,
    }))
    .sort((a, b) => a.period.localeCompare(b.period));

export const metricFromRow = (row: any): Metric => ({
  id: row.id, teamId: row.team_id ?? undefined, title: row.title,
  unit: row.unit ?? undefined, decimals: row.decimals ?? undefined,
  ownerId: row.owner_id ?? undefined, goalIds: row.goal_ids ?? [],
  entries: entriesFromRows(row.metric_entries),
  origin: row.origin ?? 'input',
  formulaExpression: row.formula_expression ?? undefined,
  aggregate: row.origin === 'aggregate' ? {
    sourceMetricId: row.aggregate_source_metric_id,
    function: row.aggregate_function,
    trailingPeriods: row.aggregate_trailing_periods ?? undefined,
    incompletePolicy: row.aggregate_incomplete_policy,
  } : undefined,
  startPeriod: row.start_period ?? undefined,
  openingValue: row.opening_value === null || row.opening_value === undefined ? undefined : Number(row.opening_value),
  catalogKey: row.catalog_key ?? undefined,
  cadence: row.cadence ?? 'weekly',
  indicator: row.indicator ?? null,
  rockIds: Array.isArray(row.rock_ids) ? row.rock_ids : [],
  targets: Array.isArray(row.targets) ? row.targets : [],
});

/**
 * Hydration and the realtime handlers used to build this shape inline, and the
 * realtime copies dropped `team_id` — so an UPDATE arriving for a board would
 * silently un-scope it from its team. One mapper, used by every path.
 */
export const scorecardFromRow = (row: any): Scorecard => ({
  id: row.id,
  teamId: row.team_id ?? undefined,
  workspaceId: row.workspace_id,
  name: row.name,
  createdAt: row.created_at,
  status: row.status === 'archived' ? 'archived' : 'active',
  archivedAt: row.archived_at ?? null,
  archivedBy: row.archived_by ?? null,
});
